import React, { useEffect, useState } from 'react'
import LayoutHeader from '../../layout/LayoutHeader'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { setErrorAlert, setSuccessAlert, setLoader } from '../../store/alert/alertSlice'
import { postDataAPI, getDataAPI } from '../../utils/fetchData'

const EmailDetectionSettings = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [categories, setCategories] = useState([])
    const [newKeyword, setNewKeyword] = useState({ category: '', keyword: '' })
    const [blockedKeywords, setBlockedKeywords] = useState([])
    const [newBlockedKeyword, setNewBlockedKeyword] = useState('')

    const categoryNames = ['Show Offers', 'Ticket Counts', 'Upcoming Releases', 'Travel Confirmations']

    const categoryDescriptions = {
        'Show Offers': 'Set the email addresses and keywords for agents, promoters etc. who send you new show offers.',
        'Ticket Counts': 'Set the email addresses and keywords for agents and promoters who send you ticket count updates.',
        'Upcoming Releases': 'Set the email addresses and keywords for labels and distributors who send you release confirmations. Auto-detect from Spotify for Artists Release Confirmations.',
        'Travel Confirmations': 'Set the email addresses and keywords for airlines, hotels, and travel agents who send you flight and hotel confirmations.'
    }

    const categoryColors = {
        'Show Offers': '#FF9500',
        'Ticket Counts': '#FFFF00',
        'Upcoming Releases': '#3B82F6',
        'Travel Confirmations': '#FF9500' // Same color as Show Offers
    }

    useEffect(() => {
        loadSettings()
    }, [])

    const loadSettings = async () => {
        try {
            setLoading(true)
            const tokenStr = localStorage.getItem('dizeeToken')
            if (!tokenStr) {
                dispatch(setErrorAlert('No authentication token found'))
                return
            }
            const token = JSON.parse(tokenStr)
            const response = await getDataAPI('user/email-detection/settings', token)
            
            console.log('Settings response:', response)
            
            if (response.data?.status === 200) {
                const data = response.data.data
                // Categories now include emailAddresses array within each category
                setCategories(data.categories || [])
                setBlockedKeywords(data.blockedKeywords || [])
            } else {
                console.error('Unexpected response status:', response.data?.status)
                dispatch(setErrorAlert(response.data?.message || 'Failed to load settings'))
            }
        } catch (error) {
            console.error('Error loading settings:', error)
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            })
            dispatch(setErrorAlert(error.response?.data?.message || 'Error loading settings'))
        } finally {
            setLoading(false)
        }
    }

    const handleSaveSettings = async () => {
        try {
            setSaving(true)
            dispatch(setLoader(true))
            
            const tokenStr = localStorage.getItem('dizeeToken')
            if (!tokenStr) {
                dispatch(setErrorAlert('No authentication token found'))
                return
            }
            const token = JSON.parse(tokenStr)
            
            // Filter out empty email addresses before saving
            const cleanedCategories = categories.map(cat => ({
                ...cat,
                emailAddresses: (cat.emailAddresses || ['']).filter(email => email.trim() !== '')
            }))
            
            console.log('Saving settings with data:', {
                categories: cleanedCategories
            })
            
            const response = await postDataAPI('user/email-detection/settings', {
                categories: cleanedCategories,
                blockedKeywords: blockedKeywords.filter(k => k && k.trim())
            }, token)
            
            console.log('Save response:', response)
            
            if (response.data?.status === 200) {
                dispatch(setSuccessAlert('Settings saved successfully'))
            } else {
                console.error('Unexpected response status:', response.data?.status)
                dispatch(setErrorAlert(response.data?.message || 'Failed to save settings'))
            }
        } catch (error) {
            console.error('Error saving settings:', error)
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                config: error.config
            })
            dispatch(setErrorAlert(error.response?.data?.message || error.message || 'Error saving settings'))
        } finally {
            setSaving(false)
            dispatch(setLoader(false))
        }
    }

    const addKeyword = (categoryName) => {
        if (!newKeyword.keyword.trim()) return
        
        setCategories(prev => prev.map(cat => {
            if (cat.name === categoryName) {
                return {
                    ...cat,
                    keywords: [...(cat.keywords || []), newKeyword.keyword.trim()]
                }
            }
            return cat
        }))
        setNewKeyword({ category: '', keyword: '' })
    }

    const removeKeyword = (categoryName, keywordIndex) => {
        setCategories(prev => prev.map(cat => {
            if (cat.name === categoryName) {
                return {
                    ...cat,
                    keywords: cat.keywords.filter((_, i) => i !== keywordIndex)
                }
            }
            return cat
        }))
    }

    const addEmailAddress = (categoryName) => {
        setCategories(prev => prev.map(cat => {
            if (cat.name === categoryName) {
                return {
                    ...cat,
                    emailAddresses: [...(cat.emailAddresses || ['']), '']
                }
            }
            return cat
        }))
    }

    const removeEmailAddress = (categoryName, emailIndex) => {
        setCategories(prev => prev.map(cat => {
            if (cat.name === categoryName) {
                const currentEmails = cat.emailAddresses || ['']
                if (currentEmails.length > 1) {
                    return {
                        ...cat,
                        emailAddresses: currentEmails.filter((_, i) => i !== emailIndex)
                    }
                }
            }
            return cat
        }))
    }

    const updateEmailAddress = (categoryName, emailIndex, value) => {
        setCategories(prev => prev.map(cat => {
            if (cat.name === categoryName) {
                const currentEmails = cat.emailAddresses || ['']
                return {
                    ...cat,
                    emailAddresses: currentEmails.map((email, i) => i === emailIndex ? value : email)
                }
            }
            return cat
        }))
    }

    // Ensure all categories exist with email addresses
    useEffect(() => {
        const existingCategoryNames = categories.map(c => c.name)
        const missingCategories = categoryNames.filter(name => !existingCategoryNames.includes(name))
        
        if (missingCategories.length > 0) {
            const defaultKeywords = {
                'Show Offers': ['show offer', 'booking', 'venue', 'performance', 'gig', 'hold', 'availability'],
                'Ticket Counts': ['ticket count', 'tickets sold', 'capacity', 'attendance', 'sellable capacity'],
                'Upcoming Releases': ['release', 'album', 'single', 'ep', 'track', 'spotify for artists'],
                'Travel Confirmations': ['flight confirmation', 'hotel confirmation', 'booking confirmation', 'itinerary', 'reservation']
            }
            
            const newCategories = missingCategories.map(name => ({
                name,
                keywords: defaultKeywords[name] || [],
                emailAddresses: ['']
            }))
            
            setCategories(prev => [...prev, ...newCategories])
        }
    }, [categories])

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] flex flex-col justify-center items-center h-screen">
                    <div className="text-white">Loading settings...</div>
                </div>
            </LayoutHeader>
        )
    }

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full flex-col items-start relative">
                        <span className="text-white text-[14px] mb-2" style={{ fontWeight: 300 }}>Email Detection Settings</span>
                        <p className="text-[rgba(255,255,255,0.6)] text-[11px] leading-[1.5] max-w-[350px]">
                            Tell Dizee which email addresses and keywords to scan.
                        </p>
                        <div className="flex items-center gap-4 absolute top-0 right-0">
                            <button
                                onClick={handleSaveSettings}
                                disabled={saving}
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate('/email')}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto pb-8">
                        <div className="space-y-5">
                            {/* Blocked Keywords Section */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                <h3 className="text-white text-[13px] font-medium mb-2">
                                    Blocked Keywords
                                </h3>
                                <p className="text-[rgba(255,255,255,0.6)] text-[11px] mb-4 leading-[1.5]">
                                    Emails with these keywords in the subject will be automatically filtered out (e.g., "advance", "marketing").
                                </p>
                                
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {blockedKeywords.length > 0 ? (
                                        blockedKeywords.map((keyword, index) => (
                                            <div 
                                                key={index}
                                                className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-[rgba(255,59,48,0.2)] border border-[rgba(255,59,48,0.4)] text-[#FF3B30]"
                                            >
                                                <span>{keyword}</span>
                                                <button
                                                    onClick={() => setBlockedKeywords(prev => prev.filter((_, i) => i !== index))}
                                                    className="hover:opacity-70 ml-1"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-[rgba(255,255,255,0.4)] text-[11px]">No blocked keywords</p>
                                    )}
                                </div>
                                
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={newBlockedKeyword}
                                        onChange={(e) => setNewBlockedKeyword(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && newBlockedKeyword.trim()) {
                                                setBlockedKeywords(prev => [...prev, newBlockedKeyword.trim()])
                                                setNewBlockedKeyword('')
                                            }
                                        }}
                                        placeholder="Add keyword to block..."
                                        className="flex-1 bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[11px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                    />
                                    <button
                                        onClick={() => {
                                            if (newBlockedKeyword.trim()) {
                                                setBlockedKeywords(prev => [...prev, newBlockedKeyword.trim()])
                                                setNewBlockedKeyword('')
                                            }
                                        }}
                                        className="text-[rgba(255,255,255,0.7)] text-[11px] px-3 py-2 rounded-lg border border-[rgba(255,255,255,0.2)] hover:border-[#CDD4DB] transition-colors"
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                            
                            {/* Category Sections */}
                            {categoryNames.map(categoryName => {
                                const category = categories.find(c => c.name === categoryName) || { name: categoryName, keywords: [] }
                                const color = categoryColors[categoryName]
                                const description = categoryDescriptions[categoryName]
                                
                                return (
                                    <div 
                                        key={categoryName}
                                        className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4"
                                    >
                                        <h3 
                                            className="text-[13px] font-medium mb-2"
                                            style={{ color: color }}
                                        >
                                            {categoryName}
                                        </h3>
                                        <p className="text-[rgba(255,255,255,0.6)] text-[11px] mb-4 leading-[1.5]">
                                            {description}
                                        </p>
                                        
                                        {/* Email Addresses for this category */}
                                        <div className="mb-4">
                                            <div className="space-y-2">
                                                {(category.emailAddresses || ['']).map((email, index) => (
                                                    <div key={index} className="flex items-center gap-2">
                                                        <input
                                                            type="email"
                                                            value={email}
                                                            onChange={(e) => updateEmailAddress(categoryName, index, e.target.value)}
                                                            placeholder="email@example.com"
                                                            className="flex-1 bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[11px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                        />
                                                        {(category.emailAddresses || ['']).length > 1 && (
                                                            <button
                                                                onClick={() => removeEmailAddress(categoryName, index)}
                                                                className="text-[rgba(255,255,255,0.5)] hover:text-white text-[16px] px-2"
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    onClick={() => addEmailAddress(categoryName)}
                                                    className="text-[rgba(255,255,255,0.7)] text-[11px] px-3 py-2 rounded-lg border border-[rgba(255,255,255,0.2)] hover:border-[#CDD4DB] transition-colors w-full"
                                                >
                                                    + Add Email
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Keywords/Tags section */}
                                        <div>
                                            <label className="text-[rgba(255,255,255,0.7)] text-[11px] mb-2 block">
                                                Tags
                                            </label>
                                            <div className="flex flex-wrap gap-2 mb-3">
                                                {category.keywords && category.keywords.length > 0 ? (
                                                    category.keywords.map((keyword, keywordIndex) => (
                                                        <div 
                                                            key={keywordIndex}
                                                            className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium"
                                                            style={{
                                                                backgroundColor: `${color}20`,
                                                                border: `1px solid ${color}40`,
                                                                color: color
                                                            }}
                                                        >
                                                            <span>{keyword}</span>
                                                            <button
                                                                onClick={() => removeKeyword(categoryName, keywordIndex)}
                                                                className="hover:opacity-70 ml-1"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))
                                                ) : null}
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={newKeyword.category === categoryName ? newKeyword.keyword : ''}
                                                    onChange={(e) => setNewKeyword({ category: categoryName, keyword: e.target.value })}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            addKeyword(categoryName)
                                                        }
                                                    }}
                                                    placeholder="Add tag..."
                                                    className="flex-1 bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[11px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                />
                                                <button
                                                    onClick={() => addKeyword(categoryName)}
                                                    className="text-[rgba(255,255,255,0.7)] text-[11px] px-3 py-2 rounded-lg border border-[rgba(255,255,255,0.2)] hover:border-[#CDD4DB] transition-colors"
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}

                            {/* Save Button */}
                            <button
                                onClick={handleSaveSettings}
                                disabled={saving}
                                className="w-full bg-[#CDD4DB] text-black text-[12px] font-medium py-3 px-4 rounded-lg hover:opacity-80 transition-opacity disabled:opacity-50 mt-2"
                            >
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    )
}

export default EmailDetectionSettings

